import React, { useState, useMemo, useEffect } from "react";
import axios from "axios";
// import { FaChartLine } from "react-icons/fa";

export default function ProfitMarginTab({ customers = [], garages = [] }) {
    const BASE_URL = process.env.REACT_APP_API_BASE_URL;

    const [selectedCustomerId, setSelectedCustomerId] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedGarageIds, setSelectedGarageIds] = useState([]);
    const [availableYearsMonths, setAvailableYearsMonths] = useState([]);
    const [selectedYear, setSelectedYear] = useState("");
    const [selectedMonths, setSelectedMonths] = useState([]);
    const [selectedEndDate, setSelectedEndDate] = useState("");
    const [profitData, setProfitData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const sortedCustomers = useMemo(
        () =>
            customers
                .map(c => ({
                    id: c.CustomerID?.toString() || "",
                    name: (c.CustomerName || "").trim()
                }))
                .sort((a, b) => a.name.localeCompare(b.name)),
        [customers]
    );

    const customerGarages = useMemo(
        () =>
            garages
                .filter(g => g.CustomerID?.toString() === selectedCustomerId)
                .sort((a, b) => (a.GarageName || "").localeCompare(b.GarageName || "")),
        [garages, selectedCustomerId]
    );

    const filteredCustomerGarages = useMemo(
        () =>
            customerGarages.filter(garage =>
                (garage.GarageName || "").toLowerCase().includes(searchTerm.toLowerCase())
            ),
        [customerGarages, searchTerm]
    );

    const allSelected = selectedGarageIds.length > 0 && selectedGarageIds.length === customerGarages.length;

    const toggleGarageSelection = (garageId) => {
        setSelectedGarageIds(prev =>
            prev.includes(garageId)
                ? prev.filter(id => id !== garageId)
                : [...prev, garageId]
        );
    };

    const toggleSelectAllGarages = () => {
        if (allSelected) {
            setSelectedGarageIds([]);
        } else {
            setSelectedGarageIds(customerGarages.map(g => g.GarageID));
        }
    };

    const handleCustomerChange = (event) => {
        const customerId = event.target.value;
        setSelectedCustomerId(customerId);
        setSelectedGarageIds([]);
        setSearchTerm("");
        setSelectedYear("");
        setSelectedMonths([]);
        setSelectedEndDate("");
        setProfitData(null);
        setError(null);
    };

    const loadAvailableYearsMonths = async () => {
        if (!selectedCustomerId) {
            setAvailableYearsMonths([]);
            setSelectedYear("");
            setSelectedMonths([]);
            setSelectedEndDate("");
            return;
        }

        try {
            const params = new URLSearchParams({ customer_id: selectedCustomerId });
            if (selectedGarageIds.length > 0) {
                params.append("garage_ids", selectedGarageIds.join(","));
            }

            const res = await axios.get(
                `${BASE_URL}/profit-margin/available-years-months/?${params.toString()}`
            );

            setAvailableYearsMonths(res.data || []);
            setSelectedYear("");
            setSelectedMonths([]);
            setSelectedEndDate("");
        } catch (err) {
            console.error(err);
            setAvailableYearsMonths([]);
        }
    };

    useEffect(() => {
        loadAvailableYearsMonths();
        setProfitData(null);
        setError(null);
    }, [selectedCustomerId, selectedGarageIds]);

    const handleYearChange = (event) => {
        const year = event.target.value;
        setSelectedYear(year);
        setSelectedMonths([]);
        setSelectedEndDate("");
        setProfitData(null);
        setError(null);
    };

    const currentYearData = availableYearsMonths.find(ym => ym.year.toString() === selectedYear);
    const availableMonthsForYear = currentYearData ? currentYearData.months : [];

    const allMonthsSelected = selectedMonths.length > 0 && selectedMonths.length === availableMonthsForYear.length;

    const toggleMonthSelection = (month) => {
        setSelectedMonths(prev =>
            prev.includes(month)
                ? prev.filter(m => m !== month)
                : [...prev, month]
        );
    };

    const toggleSelectAllMonths = () => {
        if (allMonthsSelected) {
            setSelectedMonths([]);
        } else {
            setSelectedMonths(availableMonthsForYear);
        }
    };

    const formatCurrency = (value) => {
        if (value === null || value === undefined || Number.isNaN(Number(value))) {
            return "-$0.00";
        }
        return `$${Number(value).toFixed(2)}`;
    };

    const getGarageName = (garageId) => {
        if (garageId === null || garageId === undefined || garageId === "") return "-";
        const garage = garages.find((g) => String(g.GarageID) === String(garageId));
        return garage?.GarageName || String(garageId);
    };

    const calculateProfit = async () => {
        if (!selectedCustomerId) {
            setError("Please select a customer.");
            return;
        }

        if (selectedGarageIds.length === 0) {
            setError("Please select at least one garage.");
            return;
        }

        if (!selectedYear) {
            setError("Please select a year.");
            return;
        }

        if (selectedMonths.length === 0) {
            setError("Please select at least one month.");
            return;
        }

        if (!selectedEndDate) {
            setError("Please select an end date.");
            return;
        }

        setError(null);
        setLoading(true);

        try {
            const res = await axios.post(`${BASE_URL}/profit-margin/calculate/`, {
                customer_id: selectedCustomerId,
                garage_ids: selectedGarageIds,
                year: selectedYear,
                months: selectedMonths,
                end_date: selectedEndDate,
            });

            setProfitData(res.data);
        } catch (err) {
            console.error(err);
            setError(
                err.response?.data?.detail ||
                err.response?.data?.error ||
                "Failed to calculate profit"
            );
            setProfitData(null);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen w-full bg-gray-50 p-2">
            <div className="bg-white shadow-lg rounded-xl p-4 min-h-full">
                <div className="flex flex-col gap-3 mb-2">
                    {/* <div className="flex items-center gap-3">
                        <FaChartLine className="text-blue-600 text-2xl" />
                        <h2 className="text-2xl font-semibold">Profit Margin</h2>
                    </div> */}
                </div>

                <div className="grid gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Customer
                        </label>
                        <select
                            className="w-full border border-gray-300 rounded-lg px-4 py-1.5 focus:outline-none focus:border-blue-500"
                            value={selectedCustomerId}
                            onChange={handleCustomerChange}
                        >
                            <option value="">Select a customer</option>
                            {sortedCustomers.map(customer => (
                                <option key={customer.id} value={customer.id}>
                                    {customer.name || "Unnamed Customer"}
                                </option>
                            ))}
                        </select>
                    </div>

                    {selectedCustomerId ? (
                        <>
                            <div>
                                <div className="mt-1">
                                    <label className="font-semibold block mb-2">Search Garages</label>
                                    <input
                                        type="text"
                                        placeholder="Search garages..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="border rounded text-sm p-2 w-full"
                                    />
                                </div>

                                <div className="mt-4 border rounded p-3 bg-white">
                                    <label className="flex items-center gap-3 mb-1 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={allSelected}
                                            onChange={toggleSelectAllGarages}
                                            className="h-3 w-3 text-blue-600 border-gray-300 rounded"
                                        />
                                        <span className="font-semibold">Select all garages</span>
                                    </label>

                                    {filteredCustomerGarages.length === 0 ? (
                                        <div className="p-4 text-sm text-gray-500 border rounded">
                                            No garages match this customer or search term.
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto">
                                            {filteredCustomerGarages.map((garage) => (
                                                <label
                                                    key={garage.GarageID}
                                                    className="flex items-center gap-2 p-1  hover:bg-gray-50 cursor-pointer"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedGarageIds.includes(garage.GarageID)}
                                                        onChange={() => toggleGarageSelection(garage.GarageID)}
                                                        className="h-3 w-3 text-blue-600 border-gray-300 rounded"
                                                    />
                                                    <span className="text-sm">{garage.GarageName || "Unnamed Garage"}</span>
                                                </label>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Year
                                </label>
                                <select
                                    className="w-full border border-gray-300 rounded-lg px-4 py-1.5 focus:outline-none focus:border-blue-500 disabled:cursor-not-allowed disabled:bg-gray-100"
                                    value={selectedYear}
                                    onChange={handleYearChange}
                                    disabled={!selectedCustomerId}
                                >
                                    <option value="">Select year</option>
                                    {availableYearsMonths.map((ym) => (
                                        <option key={ym.year} value={ym.year}>
                                            {ym.year}
                                        </option>
                                    ))}
                                </select>

                                {selectedCustomerId && availableYearsMonths.length === 0 && (
                                    <p className="mt-3 text-sm text-gray-500">
                                        No data found for the selected customer and garage filters.
                                    </p>
                                )}
                            </div>

                            {selectedYear && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Months
                                    </label>
                                    <div className="border rounded p-3 bg-white">
                                        <label className="flex items-center gap-3 mb-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={allMonthsSelected}
                                                onChange={toggleSelectAllMonths}
                                                className="h-3 w-3 text-blue-600 border-gray-300 rounded"
                                            />
                                            <span className="font-semibold">Select all months</span>
                                        </label>

                                        <div className="grid grid-cols-3 gap-2">
                                            {availableMonthsForYear.map((month) => (
                                                <label
                                                    key={month}
                                                    className="flex items-center gap-2 p-1 hover:bg-gray-50 cursor-pointer"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedMonths.includes(month)}
                                                        onChange={() => toggleMonthSelection(month)}
                                                        className="h-3 w-3 text-blue-600 border-gray-300 rounded"
                                                    />
                                                    <span className="text-sm">
                                                        {new Date(0, month - 1).toLocaleString('default', { month: 'long' })}
                                                    </span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {selectedYear && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        End Date
                                    </label>
                                    <input
                                        type="date"
                                        className="w-full border border-gray-300 rounded-lg px-4 py-1.5 focus:outline-none focus:border-blue-500"
                                        value={selectedEndDate}
                                        onChange={(e) => setSelectedEndDate(e.target.value)}
                                    />
                                </div>
                            )}

                            <div className="flex flex-col gap-3">
                                {error && (
                                    <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                                        {error}
                                    </div>
                                )}

                                <button
                                    onClick={calculateProfit}
                                    disabled={loading}
                                    className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-5 py-1.5 text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                    {loading ? "Calculating..." : "Check Profit"}
                                </button>
                            </div>

                            {profitData && (
                                <p className="text-red-400">*<span className="text-xs">Revenue Includes: Items total + Items tax + Shipping charges + Shipping tax + Installation subtotal + Installation tax</span></p>

                                // <p className="text-red-400">*<span className="text-xs">Revenue includes delivered item totals and item tax. Shipping and installation are included for complete quotations.</span></p>
                            )}

                            {profitData && (
                                <div className="grid gap-4 mt-1 md:grid-cols-3">
                                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                                        <p className="text-sm text-gray-500">Revenue</p>
                                        <p className="text-2xl font-semibold text-green-700">{formatCurrency(profitData.revenue)}</p>
                                    </div>
                                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                                        <p className="text-sm text-gray-500">Cost</p>
                                        <p className="text-2xl font-semibold text-orange-700">{formatCurrency(profitData.cost)}</p>
                                    </div>
                                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                                        <p className="text-sm text-gray-500">Profit</p>
                                        <p className="text-2xl font-semibold text-blue-700">{formatCurrency(profitData.profit)}</p>
                                    </div>
                                </div>
                            )}

                            {profitData && (
                                <div className="grid gap-4 mt-4 md:grid-cols-3">
                                    <div className="rounded-xl border border-gray-200 bg-white p-4">
                                        <p className="text-sm text-gray-500">Items Subtotal</p>
                                        <p className="text-lg font-semibold">{formatCurrency(profitData.items_subtotal)}</p>
                                    </div>
                                    <div className="rounded-xl border border-gray-200 bg-white p-4">
                                        <p className="text-sm text-gray-500">Items Tax</p>
                                        <p className="text-lg font-semibold">{formatCurrency(profitData.items_tax)}</p>
                                    </div>
                                    <div className="rounded-xl border border-gray-200 bg-white p-4">
                                        <p className="text-sm text-gray-500">Shipping Charge</p>
                                        <p className="text-lg font-semibold">{formatCurrency(profitData.shipping_charge)}</p>
                                    </div>
                                    <div className="rounded-xl border border-gray-200 bg-white p-4">
                                        <p className="text-sm text-gray-500">Shipping Tax</p>
                                        <p className="text-lg font-semibold">{formatCurrency(profitData.shipping_tax)}</p>
                                    </div>
                                    <div className="rounded-xl border border-gray-200 bg-white p-4">
                                        <p className="text-sm text-gray-500">Installation Subtotal</p>
                                        <p className="text-lg font-semibold">{formatCurrency(profitData.installation_subtotal)}</p>
                                    </div>
                                    <div className="rounded-xl border border-gray-200 bg-white p-4">
                                        <p className="text-sm text-gray-500">Installation Tax</p>
                                        <p className="text-lg font-semibold">{formatCurrency(profitData.installation_tax)}</p>
                                    </div>
                                </div>
                            )}

                            {profitData?.quotation_breakdowns?.length > 0 && (
                                <div className="mt-6 rounded-xl border border-gray-200 bg-white p-4">
                                    <div className="mb-4 flex items-center justify-between">
                                        <div>
                                            <h3 className="text-lg font-semibold">Revenue Breakdown</h3>
                                        </div>
                                    </div>
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Invoice</th>
                                                    <th className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Items Subtotal</th>
                                                    <th className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Items Tax(%)</th>
                                                    <th className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Items Tax</th>
                                                    <th className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Shipping Charge</th>
                                                    <th className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Shipping Tax(%)</th>
                                                    <th className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Shipping Tax</th>
                                                    <th className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Installation Subtotal</th>
                                                    <th className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Installation Tax(%)</th>
                                                    <th className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Installation Tax</th>
                                                    <th className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Quotation Revenue</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100 bg-white">
                                                {profitData.quotation_breakdowns.map((quote, index) => (
                                                    <tr key={`${quote.quotation_id}-${index}`}>
                                                        <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700">{quote.invoice_number || "-"}</td>
                                                        <td className="whitespace-nowrap px-3 py-2 text-sm text-right text-gray-700">{formatCurrency(quote.items_subtotal)}</td>
                                                        <td className="whitespace-nowrap px-3 py-2 text-sm text-right text-gray-700">{(quote.items_tax_percentage)}%</td>
                                                        <td className="whitespace-nowrap px-3 py-2 text-sm text-right text-gray-700">{formatCurrency(quote.items_tax_amount)}</td>
                                                        <td className="whitespace-nowrap px-3 py-2 text-sm text-right text-gray-700">{formatCurrency(quote.shipping_charge)}</td>
                                                        <td className="whitespace-nowrap px-3 py-2 text-sm text-right text-gray-700">{(quote.shipping_charge_percentage
                                                        )}%</td>
                                                        <td className="whitespace-nowrap px-3 py-2 text-sm text-right text-gray-700">{formatCurrency(quote.shipping_tax)}</td>
                                                        <td className="whitespace-nowrap px-3 py-2 text-sm text-right text-gray-700">{formatCurrency(quote.installation_subtotal)}</td>
                                                        <td className="whitespace-nowrap px-3 py-2 text-sm text-right text-gray-700">{(quote.installation_tax_percentage)}%</td>
                                                        <td className="whitespace-nowrap px-3 py-2 text-sm text-right text-gray-700">{formatCurrency(quote.installation_tax_amount)}</td>
                                                        <td className="whitespace-nowrap px-3 py-2 text-sm text-right text-gray-700">{formatCurrency(quote.quotation_revenue)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {profitData?.quotation_items?.length > 0 && (
                                <div className="mt-6 rounded-xl border border-gray-200 bg-white p-4">
                                    <div className="mb-4 flex items-center justify-between">
                                        <div>
                                            <h3 className="text-lg font-semibold">Items Breakdown</h3>
                                        </div>
                                    </div>
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Invoice</th>
                                                    <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Item</th>
                                                    <th className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Unit Price</th>
                                                    <th className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Delivered Qty</th>
                                                    <th className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Total</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100 bg-white">
                                                {profitData.quotation_items.map((item, index) => (
                                                    <tr key={`${item.quotation_id}-${item.item_name}-${index}`}>
                                                        <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700">{item.invoice_number || "-"}</td>
                                                        <td className="px-3 py-2 text-sm text-gray-700">{item.item_name}</td>
                                                        <td className="whitespace-nowrap px-3 py-2 text-sm text-right text-gray-700">{formatCurrency(item.unit_price)}</td>
                                                        <td className="whitespace-nowrap px-3 py-2 text-sm text-right text-gray-700">{item.quantity}</td>
                                                        <td className="whitespace-nowrap px-3 py-2 text-sm text-right text-gray-700">{formatCurrency(item.total_price)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {profitData?.quotation_installation_expenses?.length > 0 && (
                                <div className="mt-6 rounded-xl border border-gray-200 bg-white p-4">
                                    <div className="mb-4 flex items-center justify-between">
                                        <div>
                                            <h3 className="text-lg font-semibold">Installation / Labour Expenses</h3>
                                        </div>
                                    </div>
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Invoice</th>
                                                    <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Expense Name</th>
                                                    <th className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Qty</th>
                                                    <th className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Price</th>
                                                    <th className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Total</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100 bg-white">
                                                {profitData.quotation_installation_expenses.map((expense, index) => (
                                                    <tr key={`${expense.quotation_id}-${expense.expense_name}-${index}`}>
                                                        <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700">{expense.invoice_number || "-"}</td>
                                                        <td className="px-3 py-2 text-sm text-gray-700">{expense.expense_name}</td>
                                                        <td className="whitespace-nowrap px-3 py-2 text-sm text-right text-gray-700">{expense.expense_quantity}</td>
                                                        <td className="whitespace-nowrap px-3 py-2 text-sm text-right text-gray-700">{formatCurrency(expense.price)}</td>
                                                        <td className="whitespace-nowrap px-3 py-2 text-sm text-right text-gray-700">{formatCurrency(expense.total_price)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {profitData?.expenditure_logs?.length > 0 && (
                                <div className="mt-6 rounded-xl border border-gray-200 bg-white p-4">
                                    <div className="mb-4 flex items-center justify-between">
                                        <div>
                                            <h3 className="text-lg font-semibold">Expenditure Logs</h3>
                                        </div>
                                    </div>
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Garage Name</th>
                                                    <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Item</th>
                                                    <th className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Qty</th>
                                                    <th className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Amount Spent</th>
                                                    <th className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Total Cost</th>
                                                    <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Purchase Date</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100 bg-white">
                                                {profitData.expenditure_logs.map((log, index) => (
                                                    <tr key={`${log.item_name}-${index}`}>
                                                        <td className="px-3 py-2 text-sm text-gray-700">{getGarageName(log.garage_id)}</td>
                                                        <td className="px-3 py-2 text-sm text-gray-700">{log.item_name}</td>
                                                        <td className="whitespace-nowrap px-3 py-2 text-sm text-right text-gray-700">{log.quantity}</td>
                                                        <td className="whitespace-nowrap px-3 py-2 text-sm text-right text-gray-700">{formatCurrency(log.amount_spent)}</td>
                                                        <td className="whitespace-nowrap px-3 py-2 text-sm text-right text-gray-700">{formatCurrency(log.total_spent)}</td>
                                                        <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700">{log.purchase_date ? new Date(log.purchase_date).toLocaleDateString() : "-"}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="rounded-xl border border-dashed border-gray-300 p-4 text-sm text-gray-500">
                            Choose a customer to load the related garage list.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
